<template>
<m-pageframe
    title="Administrative Functions"
>
<v-container>
    <v-text-field
        autofocus
        prepend-icon="mdi-magnify"
        v-model="full_text"
        @keydown.enter="onRunFirst"
        autocomplete="off"
    />

<div
    style="text-align: center"
>

    <div
        v-for="(page, key) in admin_pages"
        :key="key"
    >
        <div
            class="admin_button"
            v-show="inSearch(page)"
            v-on:click="gotoPage(page)"
        >
            <div
                style="flex-grow: 1; font-weight: bold;"
            >
                {{page.text}}
            </div>

            <div>
                 <v-icon
                    v-if="locked(page)"
                 >
                    mdi-lock-outline
                 </v-icon>
                 <v-icon
                     v-if="unlocked(page)"
                 >
                    mdi-lock-open-variant-outline
                 </v-icon>
            </div>
        </div>
    </div>
</div>

<!-- Easter Egg Snake Game -->
<v-dialog
    v-model="showGame"
    max-width="600px"
    persistent
    :retain-focus="false"
    content-class="game-dialog"
>
    <m-admin-game @close="showGame = false" />
</v-dialog>

</v-container>
</m-pageframe>
</template>

<script>
import MAdminGame from '@/components/admin/m-admin-snake-game.vue';

export default {
    components: {
        MAdminGame
    },

    data() {
        return {
            full_text: null,
            showGame: false,
            konamiSequence: [],
            konamiCode: ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'],

            admin_pages: [
                { 'text': 'Employees', 'page': 'admin-search-employees' },
                { 'text': 'Organizations', 'page': 'admin-search-organizations' },
                { 'text': 'RedChirp Phone Numbers', 'page': 'admin-search-redchirp-numbers' },
                { 'text': 'Reset Test Data', 'page': 'admin-setup-demo', roles: ['administrator','developer'] },
                { 'text': 'Reports', 'page': 'admin-reports' },
                { 'text': 'Widget Analytics', 'page': 'admin-widget-analytics' },
                { 'text': 'Uptime', 'page': 'admin-uptime', roles: ['administrator','developer'] },
                { 'text': 'RedChirp Cron (Scheduled Tasks and Processors)', 'page': 'admin-processors', roles: ['administrator','developer'] },
                { 'text': 'Tuning Knobs (Developers)', 'page': 'admin-knobs', roles: ['administrator','developer'] },
                { 'text': 'Built in Rate Cards', 'page': 'admin-built-in-rate-cards', roles: ['administrator','developer', 'finance'] },
                { 'text': 'Intuit', 'page': 'admin-intuit', roles: ['admin','developer', 'finance'] },
                { 'text': 'FreshDesk Integration', 'page': 'admin-freshdesk', roles: ['administrator','developer'] },
                { 'text': 'Organization Billing Settings', 'page': 'admin-billing-search-organizations', roles: ['admin','developer', 'finance'] },
                { 'text': 'Billing', 'page': 'admin-search-invoices', roles: ['admin','developer', 'finance'] },
                { 'text': 'RedChirp Monthly Recurring Revenue (MRR)', 'page': 'admin-mrr', roles: ['admin','developer', 'finance'] },
                { 'text': 'RedChirp Weekly Sales Forecast', 'page': 'admin-sales-forecast' },
                { 'text': 'RedChirp Net Revenue Retention (NRR)', 'page': 'admin-nrr', roles: ['admin','developer', 'finance'] },
                { 'text': 'Incidents (Outages)', 'page': 'admin-search-incidents', roles: ['admin','developer'] },
                { 'text': 'Rest API Reference', 'page': 'admin-search-rest-reference', roles: ['admin','developer'] },
                { 'text': 'Typeform Integration', 'page': 'admin-search-typeform' },
                { 'text': 'Stripe Customers / Payment methods', 'page': 'admin-redchirp-stripe-search', roles: ['admin','developer', 'finance'] },
                { 'text': 'Reset Demo Account', 'page': 'admin-multidemo' },
                { 'text': 'Process History', 'page': 'admin-process-history' },
                { 'text': 'Alerts', 'page': 'admin-search-alerts' },
                { 'text': 'Admin Link Tree', 'page': 'admin-link-trees' },
                { 'text': 'Secrets', 'page': 'admin-secrets' },
                { 'text': 'Admin SSO Settings', 'page': 'admin-red-chirp-sso-settings', roles: ['administrator','developer'] },
                { 'text': 'TCR Campaigns', 'page': 'admin-search-tcr-campaigns' },
                { 'text': 'TCR Events', 'page': 'admin-search-tcr-events' },
                { 'text': 'Calendar', 'page': 'admin-calendar' },
                { 'text': 'Sales After Booking Demo Survey', 'page': 'admin-sales-after-booking-demo-surveys' },
                { 'text': 'Engagement / Retention', 'page': 'admin-engagement-home' },
                { 'text': 'RedChirp Staff Roles', 'page': 'admin-red-chirp-staff-roles' },
                { 'text': 'Phone Number Tools', 'page': 'admin-tn-tools' },
                { 'text': 'Password Reset Requests', 'page': 'admin-password-reset-links' },
                { 'text': 'Bulk Message Templates', 'page': 'admin-bulk-message-templates' },
                { 'text': 'Separate Customers and Prospects Tool', 'page': 'admin-separate-customers-prospects' },
                { 'text': 'Shortened Link Domains', 'page': 'admin-shortened-link-domains', roles: ['administrator','developer'] },
                { 'text': 'Stock Images', 'page': 'admin-stock-images', roles: ['administrator','developer'] },
                { 'text': 'Texas Do Not Call List', 'page': 'admin-texas-dnc-list', roles: ['administrator','developer'] }
            ]
        }
    },

    mounted() {
        // Add event listener for Konami code
        window.addEventListener('keydown', this.checkKonamiCode);
    },

    beforeDestroy() {
        // Remove event listener when component is destroyed
        window.removeEventListener('keydown', this.checkKonamiCode);
    },

    methods: {
        gotoPage(page)
        {
            if ( this.locked(page) )
            {
                this.$rc.alert('Access Denied','You must have one of the following RedChirp staff roles to access this page: '+page.roles);
                return;
            }

            if ( page.page == 'admin-bulk-message-templates' )
            {
                this.$router.push({
                    'name': 'admin-bulk-message-templates',
                    'params' : {
                        'organization_id': this.$rc.$state.organization_id,
                    },
                    'query': {
                        'admin': true,
                    },
                });
                return;
            }

            this.$router.push({'name': page.page}).catch(e=>{}); // swallow any exception
        },

        inSearch(page)
        {
            if ( this.full_text == null ) return true;
            return page.text.toLowerCase().includes(this.full_text.toLowerCase());
        },

        locked(item)
        {
            if ( item.roles == null ) return false;
            return !this.$rc.$state.hasRedChirpStaffRole(item.roles);
        },

        unlocked(item)
        {
            if ( item.roles == null ) return false;
            return this.$rc.$state.hasRedChirpStaffRole(item.roles);
        },

        onRunFirst()
        {
            for ( let i = 0; i < this.admin_pages.length; i++ )
            {
                let page = this.admin_pages[i];
                if ( this.inSearch(page) )
                {
                    this.gotoPage(page);
                    return;
                }
            }
        },

        // Konami code detector
        checkKonamiCode(e) {
            // Get the key name
            const key = e.key.toLowerCase();

            // Add the key to the sequence
            this.konamiSequence.push(key);

            // Keep only the last N keys where N is the length of the Konami code
            if (this.konamiSequence.length > this.konamiCode.length) {
                this.konamiSequence.shift();
            }

            // Check if the sequence matches the Konami code
            const isKonamiCode = this.konamiSequence.every((key, index) => {
                return key.toLowerCase() === this.konamiCode[index].toLowerCase();
            });

            // If the Konami code is entered, show the game
            if (isKonamiCode && this.konamiSequence.length === this.konamiCode.length) {
                this.showGame = true;
                this.konamiSequence = []; // Reset the sequence
            }
        }
    },
}
</script>

<style lang="scss" scoped>
.admin_button
{
    border: 1px solid rgba(0,0,0,0.1); 
    padding-left: 15px; 
    padding-right: 15px; 
    padding-top: 20px; 
    padding-bottom: 20px; 
    border-radius: 5px; 
    margin-bottom: 7px; 
    text-align: left; 
    display: flex; cursor: pointer;
}

.admin_button:hover
{
    text-decoration: underline;
}
</style>
